import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';

import { TourCalloutComponent } from '@echospaces-web-monorepo/es-ui-next';

import { PortalTourService } from './portal-tour.service';

/**
 * How far the spotlight ring stands off the element it frames (Figma: 8px around the nav).
 */
const SPOTLIGHT_OUTSET = 8;

interface SpotlightRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The guided tour on screen: the dimmed page with a spotlight on the element the step explains,
 * and the callout beside it. Steps whose element is not on screen show the callout centred.
 */
@Component({
  selector: 'es-portal-tour',
  imports: [TourCalloutComponent],
  templateUrl: './portal-tour.component.html',
  styleUrl: './portal-tour.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'skip()' },
})
export class PortalTourComponent {
  protected readonly tour = inject(PortalTourService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * The element the current step explains, while it is on screen.
   */
  protected readonly target = signal<HTMLElement | null>(null);
  protected readonly spotlight = signal<SpotlightRect | null>(null);

  protected readonly isLast = computed(() => this.tour.step() === this.tour.total);
  protected readonly nextLabel = computed(() => (this.tour.step() === 1 ? 'Show me around' : 'Next'));
  protected readonly skipLabel = computed(() => (this.isLast() ? 'Not now' : 'Skip tour'));

  constructor() {
    effect((onCleanup) => {
      const step = this.tour.currentStep();
      this.target.set(null);
      this.spotlight.set(null);
      if (!step || typeof window === 'undefined') return;

      const anchor = step.anchor;
      let frame = 0;
      let scrolled = false;
      const update = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const found = anchor ? this.findTarget(anchor) : null;
          if (found && !scrolled && !isOnScreen(found)) {
            scrolled = true;
            found.scrollIntoView?.({ block: 'center', inline: 'nearest' });
          }
          this.track(found && isOnScreen(found) ? found : null);
        });
      };

      // Pages load lazily after the tour navigates, so keep looking for the anchor as the DOM changes.
      const observer = anchor && typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null;
      observer?.observe(document.body, { childList: true, subtree: true });
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      update();
      this.focusCallout();

      onCleanup(() => {
        cancelAnimationFrame(frame);
        observer?.disconnect();
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      });
    });
  }

  protected skip(): void {
    if (this.tour.step() !== null) this.tour.stop();
  }

  private findTarget(anchor: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
  }

  private track(element: HTMLElement | null): void {
    if (this.target() !== element) this.target.set(element);
    if (!element) {
      this.spotlight.set(null);
      return;
    }
    const rect = element.getBoundingClientRect();
    this.spotlight.set({
      top: Math.round(rect.top - SPOTLIGHT_OUTSET),
      left: Math.round(rect.left - SPOTLIGHT_OUTSET),
      width: Math.round(rect.width + SPOTLIGHT_OUTSET * 2),
      height: Math.round(rect.height + SPOTLIGHT_OUTSET * 2),
    });
  }

  /**
   * Keyboard users land on the step's primary action, so Enter moves the tour on.
   */
  private focusCallout(): void {
    requestAnimationFrame(() => {
      const buttons = this.host.nativeElement.querySelectorAll<HTMLButtonElement>('esn-tour-callout button');
      buttons.item(buttons.length - 1)?.focus();
    });
  }
}

function isOnScreen(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}
